import { useEffect, useMemo, useRef, useState } from 'react';

import { DraggableItem } from '@bringg-frontend/draggable-list-selector';
import { DetailedColumn, ViewColumn } from '@bringg-frontend/monitoring-shared';
import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { hasFeatureFlag } from '@bringg-frontend/global-stores';
import { GroupData, ResponseItem } from '@bringg/types';
import i18next from 'i18next';

import { useStores } from 'bringg-web/recipes';
import { ORDER_DETAILS_PANEL_SIZES_KEY } from '../orders-details';
import DriverCell from '../cell-renderers/driver-cell/driver-cell';
import { columnsProvider } from '../../../../routes-monitoring-screen/utils/details-columns-provider';
import StatusCell from '../cell-renderers/status-cell/status-cell';
import TimeWindowCell from '../cell-renderers/time-window-cell';
import TimeCell from '../cell-renderers/time-cell';
import OrderTagCell from '../cell-renderers/order-tag/order-tag-cell';
import DateCell from '../cell-renderers/date-cell';
import NextStopCell from '../cell-renderers/next-stop-cell';
import { detailsHeaders } from '../../../use-order-explorer-translations';
import RequiredSkillsCell from '../cell-renderers/required-skills-cell';
import { cellsProvider } from '../cell-renderers/cells-provider';

export const ORDER_DETAILS_COLUMNS_KEY = 'order-details-columns';

export interface ColumnConfigItem {
	id: string;
	required?: boolean;
}

export const defaultColumns: ColumnConfigItem[] = [
	{ id: 'id' },
	{ id: 'priority' },
	{ id: 'assignedTo' },
	{ id: 'status' },
	{ id: 'lastWayPointCustomerName' },
	{ id: 'timeWindow' },
	{ id: 'lastWayPointEta' },
	{ id: 'lastWayPointScheduledAt' }
];

const {
	priority,
	assignedTo,
	status,
	timeWindow,
	orderTag,
	tag,
	rank,
	servicePlanName,
	reservedUntil,
	requiredSkills,
	readyForPickup,
	orderCreated,
	groupFollowers,
	taskTypeId,
	nextStop,
	lastWayPointEta,
	lastWayPointCustomerName,
	lastWayPointScheduledAt,
	firstWayPointAddressType,
	lastWayPointAddressType
} = detailsHeaders;

export const getGeneralColumns = (): DetailedColumn<ResponseItem, GroupData>[] => [
	{
		id: 'id',
		Header: 'Order Id',
		accessor: cellsProvider.externalId
	},
	{
		id: 'priority',
		Header: i18next.t(priority),
		accessor: cellsProvider.priority
	},
	{
		id: 'assignedTo',
		Header: i18next.t(assignedTo),
		Cell: DriverCell
	},
	{
		id: 'status',
		Header: i18next.t(status),
		Cell: StatusCell
	},
	{
		id: 'lastWayPointCustomerName',
		Header: i18next.t(lastWayPointCustomerName),
		accessor: cellsProvider.lastWayPointCustomerName
	},
	{
		id: 'timeWindow',
		Header: i18next.t(timeWindow),
		Cell: TimeWindowCell
	},
	{
		id: 'lastWayPointEta',
		Header: i18next.t(lastWayPointEta),
		Cell: ({ row }) => {
			const task = getRootEnv().dashboardSdk.sdk.v2.tasks.get(row.id);
			const { way_points } = task;
			const last = way_points?.[way_points?.length - 1];

			return <TimeCell date={last?.eta} task={task} />;
		}
	},
	{
		id: 'lastWayPointScheduledAt',
		Header: i18next.t(lastWayPointScheduledAt),
		Cell: ({ row }) => {
			const task = getRootEnv().dashboardSdk.sdk.v2.tasks.get(row.id);
			const { way_points } = task;
			const last = way_points?.[way_points?.length - 1];

			return <TimeCell date={last?.scheduled_at} task={task} />;
		}
	},
	{
		id: 'firstWayPointAddressType',
		Header: i18next.t(firstWayPointAddressType),
		accessor: cellsProvider.firstWayPointAddressType
	},
	{
		id: 'lastWayPointAddressType',
		Header: i18next.t(lastWayPointAddressType),
		accessor: cellsProvider.lastWayPointAddressType
	},
	{
		id: 'orderTag',
		Header: i18next.t(orderTag),
		Cell: OrderTagCell
	},
	{
		id: 'tag',
		Header: i18next.t(tag),
		accessor: cellsProvider.tag
	},
	{
		id: 'servicePlanName',
		Header: i18next.t(servicePlanName),
		accessor: cellsProvider.servicePlanName
	},
	{
		id: 'reservedUntil',
		Header: i18next.t(reservedUntil),
		Cell: ({ row }) => {
			const task = getRootEnv().dashboardSdk.sdk.v2.tasks.get(row.id);

			return <TimeCell date={task.reserved_until} task={task} />;
		}
	},
	{
		id: 'requiredSkills',
		Header: i18next.t(requiredSkills),
		Cell: RequiredSkillsCell
	},
	{
		id: 'rank',
		Header: i18next.t(rank),
		accessor: cellsProvider.rank
	},
	{
		id: 'readyForPickup',
		Header: i18next.t(readyForPickup),
		Cell: ({ row }) => {
			const task = getRootEnv().dashboardSdk.sdk.v2.tasks.get(row.id);

			return <TimeCell date={task.preparation_ready_for_pickup_time_actual} task={task} />;
		}
	},
	{
		id: 'orderCreated',
		Header: i18next.t(orderCreated),
		Cell: ({ row }) => {
			const task = getRootEnv().dashboardSdk.sdk.v2.tasks.get(row.id);

			return <DateCell date={task.created_at} task={task} />;
		}
	},
	{
		id: 'groupFollowers',
		Header: i18next.t(groupFollowers),
		accessor: cellsProvider.groupFollowers
	},
	{
		id: 'taskTypeId',
		Header: i18next.t(taskTypeId),
		accessor: cellsProvider.taskTypeId
	},
	{
		id: 'nextStop',
		Header: i18next.t(nextStop),
		Cell: NextStopCell
	}
];

export const useColumns = (
	groupId: string,
	columnsLayoutMap: Map<string, number>,
	onColumnsChange: (sizeRatioChange: number) => void
) => {
	const spreadEven = useRef(false);
	const {
		usersStore: { currentUser }
	} = useStores();
	const columnConfigEnabled = hasFeatureFlag(currentUser, 'routes_monitoring_columns_config_enabled');

	const [selectedColumns, setSelectedColumns] = useState<ColumnConfigItem[]>([]);
	const [columnsToView, setColumnsToView] = useState<ViewColumn<ResponseItem, GroupData>[]>([]);
	const [columnsConfig, setColumnsConfig] = useState<{
		selectedValues: DraggableItem[];
		unselectedValues: DraggableItem[];
	}>({ selectedValues: [], unselectedValues: [] });

	const availableColumns = useMemo<DetailedColumn<ResponseItem, GroupData>[]>(() => getGeneralColumns(), []);

	useEffect(() => {
		let userColumnConfig: ColumnConfigItem[];

		if (columnConfigEnabled) {
			userColumnConfig = columnsProvider.getUserColumnConfig(ORDER_DETAILS_COLUMNS_KEY) || defaultColumns;
		} else {
			userColumnConfig = availableColumns.map(column => ({ id: column.id }));
		}

		setSelectedColumns(userColumnConfig);
	}, []);

	useEffect(() => {
		const config = columnsProvider.calculateColumnConfig(availableColumns, selectedColumns);

		setColumnsConfig(config);

		const { columns, changeRatio } = columnsProvider.calculateVisibleColumns(
			groupId,
			selectedColumns,
			availableColumns,
			columnsLayoutMap,
			spreadEven.current,
			ORDER_DETAILS_PANEL_SIZES_KEY
		);

		spreadEven.current = false;

		columns.length && columnsLayoutMap.size > 0 && onColumnsChange(changeRatio);
		setColumnsToView(columns);
	}, [availableColumns, selectedColumns]);

	const getDefaultConfig = () => {
		spreadEven.current = true;
		return columnsProvider.calculateColumnConfig(availableColumns, defaultColumns);
	};

	const onSelectedColumnsChange = (columns: DraggableItem[]) => {
		const selectedConfig = columns.map(({ id, required }) => ({ id, required }));

		setSelectedColumns(selectedConfig);
		columnsProvider.saveUserColumnConfig(ORDER_DETAILS_COLUMNS_KEY, selectedConfig);
	};

	return { columnsToView, columnsConfig, onSelectedColumnsChange, getDefaultConfig };
};
